import type { BlockType } from '@unifyapps/defs/types/block';
import {
  UA_NOCODE_EVENT_HANDLED_BLOCK_TYPE_KEY,
  UA_NOCODE_EVENT_HANDLED_KEY,
} from '@unifyapps/style/themes/consts';
import type { SyntheticEvent } from 'react';

/**
 * Marks a DOM event as handled by a no-code block.
 *
 * This is used instead of stopPropagation() to allow event bubbling while
 * signaling to parent components (e.g., Card header collapse toggle) that
 * a child block has already processed this event.
 *
 * @param event - The React synthetic event to mark as handled
 */
export function markEventAsHandledByBlock({
  blockType,
  event,
}: {
  blockType: BlockType['component']['componentType'];
  event?: SyntheticEvent;
}): void {
  // Mark on native event so it persists across React's event pooling
  // and is accessible to parent event handlers
  if (event) {
    (event.nativeEvent as unknown as Record<string, unknown>)[UA_NOCODE_EVENT_HANDLED_KEY] = true;
    if (blockType) {
      (event.nativeEvent as unknown as Record<string, unknown>)[
        UA_NOCODE_EVENT_HANDLED_BLOCK_TYPE_KEY
      ] = blockType;
    }
  }
}

/**
 * Checks if a DOM event was already handled by a no-code block.
 *
 * Parent components should call this to determine if they should
 * skip processing an event that was already handled by a child block.
 *
 * @param event - The React synthetic event to check
 * @returns true if the event was handled by a child block
 */
export function isEventHandledByBlock(event?: SyntheticEvent): boolean {
  if (event) {
    return (
      (event.nativeEvent as unknown as Record<string, unknown>)[UA_NOCODE_EVENT_HANDLED_KEY] ===
      true
    );
  }
  return false;
}

export function getEventHandledBlockType(
  event?: SyntheticEvent,
): BlockType['component']['componentType'] | undefined {
  if (event) {
    return (event.nativeEvent as unknown as Record<string, unknown>)[
      UA_NOCODE_EVENT_HANDLED_BLOCK_TYPE_KEY
    ] as BlockType['component']['componentType'];
  }
  return undefined;
}
